import { useCallback, type ReactNode } from 'react'
import Link from '@/components/link-component'
import RichText from '@/components/ui/rich-text'
import DynamicAction, { type DynamicActionProps } from '@/components/ui/dynamic-action'
import { cx } from '@/utils/strings'
import type { Entry } from 'contentful'
import type { TypeComponentPricingSkeleton } from '@/types/ctf'
import HighlightTerms from '@/components/ui/highlight-terms'
import CtaLink from './cta-link'
import ImageContentful from './image-contentful'
import { buttonStyles } from './cta-link'

type PricingCardEntry = Entry<TypeComponentPricingSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

export type PricingCardProps = PricingCardEntry['fields'] & {
  hasShadow?: boolean
  highlightTerms?: Array<{ id: string; key: string; value: string }>
  hasMultipleActionCards?: boolean
}

export default function PricingCard({
  highlightTerms,
  headline,
  subHeading,
  highlightCard = false,
  actionsPosition = 'Bottom',
  currency,
  pricePretext,
  price,
  priceDivider = false,
  period,
  periodDetail,
  subtextHeader,
  subtext,
  features,
  bottomText,
  actions = [],
  columnHeading,
  setCardAsLink = false,
  image,
  hasMultipleActionCards = false,
}: PricingCardProps): JSX.Element {
  const cardWrapCss = cx(
    'mx-auto mb-4 p-6 max-w-2xl rounded-lg flex flex-col h-full w-full border',
    !highlightCard && 'bg-white dark:bg-secondary-800',
    highlightCard && 'bg-primary-500 dark:bg-primary-600 text-white'
  )

  const cardWrapHoverStyle = [
    'relative transition-transform duration-300 ease-out',
    "md:hover:scale-[1.01] md:before:content-[''] md:hover:before:opacity-30",
    'before:rounded-lg before:absolute before:-z-10 before:top-0 before:left-0 before:h-full before:w-full',
    'before:shadow-[0px_0px_16px_0px_var(--color-primaryBlue),_0px_0px_4px_0px_var(--color-accent-violet)]',
    'before:opacity-0 md:hover:before:opacity-30 before:transition-opacity before:duration-[250ms] before:ease-out',
  ].join(' ')

  const LinkWrap = useCallback(
    ({ children, ctas }: { children: ReactNode; ctas: any }) => (
      <>
        {columnHeading && <div className="xl:hidden text-xl py-2 text-white">{columnHeading}</div>}
        {setCardAsLink && ctas && ctas.length === 1 && ctas[0].fields.link ? (
          <Link
            className={cx('group', cardWrapCss, cardWrapHoverStyle, ctas[0].fields.plausibleEventName)}
            to={ctas[0].fields.link.fields.url}
            aria-label={`go to ${ctas[0].fields.link.fields.url}`}
          >
            {children}
          </Link>
        ) : (
          <div className={cardWrapCss}>{children}</div>
        )}
      </>
    ),
    []
  )

  const renderActions = () => (
    <div className={cx(hasMultipleActionCards && 'flex flex-col items-center justify-start gap-6')}>
      {actions.map((action) => {
        if (!action) return null
        const type = action.sys.contentType.sys.id
        const fields = {
          type,
          ...action.fields,
        } as DynamicActionProps

        return setCardAsLink ? (
          <CtaLink
            style={highlightCard ? buttonStyles.btnSolidSecondary : fields.style}
            key={action.sys.id}
            align="Center"
          >
            {fields.label}
          </CtaLink>
        ) : (
          <DynamicAction key={action.sys.id} {...fields} align="Center" style={fields.style} />
        )
      })}
    </div>
  )

  return (
    <LinkWrap ctas={actions}>
      <div className="flex flex-col h-full">
        <div
          className={cx('flex flex-col gap-6 flex-grow', !highlightCard ? 'text-black dark:text-white' : 'text-white')}
        >
          <h4 className="h6 mb-0 pb-0" data-testid="pricing-headline">
            {headline}
          </h4>
          {subHeading && (
            <div className="[&_p]:mb-0">
              <RichText body={subHeading} />
            </div>
          )}
          {/* Price and period / period detail */}

          {price ? (
            <div className={pricePretext && '-mt-5'}>
              {pricePretext && (
                <div className={cx(highlightCard ? 'text-secondary-50' : ' text-primaryBlue', 'h-5 text-sm')}>
                  {pricePretext}
                </div>
              )}
              <div
                className={cx(
                  'flex flex-row items-center gap-4 h-17',
                  priceDivider && 'py-13 border-t border-b',
                  priceDivider && highlightCard && 'border-primary-500 dark:border-secondary-800',
                  priceDivider && !highlightCard && 'border-secondary-100'
                )}
              >
                <div className={cx('', !highlightCard && 'text-primaryBlue')} data-testid="pricing-price">
                  <div className="font-[500]">
                    {currency?.trim() && (
                      <sup className="top-[-0.1rem] text-[4rem] leading-[4rem]" data-testid="pricing-currency">
                        {currency}
                      </sup>
                    )}
                    <span className="leading-[4rem] text-[4rem]">{price}</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 text-lg">
                  {period && <span data-testid="pricing-period">{period}</span>}
                  {periodDetail && <span data-testid="pricing-periodDetail">{periodDetail}</span>}
                </div>
              </div>
            </div>
          ) : (
            image && <ImageContentful className="h-17 rounded-lg" {...image.fields} />
          )}
          {actions && actionsPosition === 'Middle' && renderActions()}
          {subtext && (
            <span className="text-lg" data-testid="pricing-subtext">
              {subtextHeader && <span className="font-semibold block">{subtextHeader}</span>}
              {subtext}
            </span>
          )}

          {features && (
            <div className="flex">
              <ul className="list-none text-lg" data-testid="pricing-features">
                {features.map((feature) => (
                  <li className="mb-4 last:mb-0 flex flex-row items-center" key={feature}>
                    <span
                      className={cx(
                        'flex justify-center items-center font-bwi text-lg mr-2',
                        !highlightCard && 'text-primaryBlue',
                        highlightCard && 'text-white'
                      )}
                    >
                      <span>&#xe912;</span>
                    </span>{' '}
                    <div className="[&_button]:py-0!">
                      <HighlightTerms text={feature} terms={highlightTerms} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {bottomText && (
            <span
              className={cx(
                'text-sm m-0',
                !highlightCard && 'text-secondary-600 dark:text-secondary-200',
                highlightCard && 'text-secondary-200'
              )}
              data-testid="pricing-bottom-text"
            >
              <HighlightTerms text={bottomText} terms={highlightTerms} />
            </span>
          )}
        </div>
        {actions && (actionsPosition === 'Bottom' || actionsPosition === null) && (
          <div
            className={cx('pt-6 mt-auto flex items-start justify-center', hasMultipleActionCards && 'xl:min-h-[120px]')}
          >
            {renderActions()}
          </div>
        )}
      </div>
    </LinkWrap>
  )
}
